// REFINANCIAMENTO NO MESMO BANCO — regras puras, sem I/O.
//
// Refinanciar e' renegociar com o PROPRIO credor: o banco quita um ou mais
// contratos ativos dele e emite um novo, com prazo/parcela novos e, se o
// convenio permitir, um troco (valor novo menos o saldo de quitacao). Nao e'
// portabilidade — ali o credor muda e a regra e' o inverso (destino != credor).
//
// A negociacao (pedido do servidor, oferta/contraproposta do banco, aceite)
// vive fora do contrato. O contrato sucessor so' nasce quando os dois lados
// concordaram; dai em diante ele segue o fluxo de todo REFIN: averbacao,
// aplicacao da ADF e, nela, a origem marcada `Substituído`.
import { motivoContratoNaoPortavel } from "./contrato.js";
export const ESTADOS_REFINANCIAMENTO = [
    "solicitado",
    "ofertado",
    "aceito",
    "efetivado",
    "recusado",
    "cancelado",
    "expirado",
];
/** Negociacao aberta: prende as origens e expira pelo prazo de validade. */
export const ESTADOS_REFIN_ABERTOS = ["solicitado", "ofertado"];
const TRANSICOES = {
    solicitado: {
        banco_aprova: "aceito",
        banco_contrapropoe: "ofertado",
        banco_recusa: "recusado",
        cancelar: "cancelado",
        expirar: "expirado",
    },
    ofertado: {
        servidor_aceita: "aceito",
        servidor_recusa: "recusado",
        cancelar: "cancelado",
        expirar: "expirado",
    },
    aceito: { efetivar: "efetivado", desfazer: "cancelado" },
    efetivado: {},
    recusado: {},
    cancelado: {},
    expirado: {},
};
/** Estado depois do evento, ou `null` quando a transicao nao existe. */
export function proximoEstadoRefin(estado, evento) {
    return TRANSICOES[estado]?.[evento] ?? null;
}
export function refinAberto(estado) {
    return ESTADOS_REFIN_ABERTOS.includes(estado);
}
export const REGRAS_REFIN_PADRAO = {
    permitido: true,
    minParcelasPagas: 0,
    permiteTroco: true,
    trocoMinimo: 0,
    permiteUnificar: true,
    validadeHoras: 48,
};
/** Completa a regra gravada (pode faltar campo em convenio antigo) com o padrao. */
export function regrasRefinDe(parcial) {
    return { ...REGRAS_REFIN_PADRAO, ...(parcial ?? {}) };
}
/**
 * Por que este contrato NAO pode ser origem de refinanciamento com `bancoId`.
 * `null` quando pode.
 *
 * Reaproveita o portao da portabilidade para o contrato em si (ativo em folha,
 * emprestimo, parcela a vencer, saldo) — as duas operacoes quitam a mesma
 * divida. Conflito com outra operacao em andamento depende dos OUTROS contratos
 * e e' respondido por quem chama.
 */
export function motivoOrigemNaoRefinanciavel(origem, regras, ctx) {
    if (!regras.permitido)
        return "O convênio deste contrato não permite refinanciamento.";
    const proprio = motivoContratoNaoPortavel(origem);
    if (proprio)
        return proprio.replace(/portabilidade/gi, "refinanciamento");
    if (origem.bancoId == null || Number(origem.bancoId) !== Number(ctx.bancoId)) {
        return "Refinanciamento é com o próprio credor. Para trocar de banco, use a portabilidade.";
    }
    if (origem.parcelasPagas < regras.minParcelasPagas) {
        return `O convênio exige pelo menos ${regras.minParcelasPagas} parcela(s) paga(s) antes de refinanciar.`;
    }
    if (ctx.quantidadeOrigens > 1 && !regras.permiteUnificar) {
        return "O convênio não permite unificar contratos num refinanciamento.";
    }
    return null;
}
/**
 * Sob qual convenio este contrato e' refinanciado — ou por que nao ha' um (#232).
 *
 * Contrato averbado pela Atlas traz o proprio convenio. Contrato LEGADO nasce
 * sem (`convenioId: ""`): e' do banco pelo credor que a rubrica aponta
 * (ADR-0010), nao por convenio. Para ele, o convenio e' o par (banco, unidade
 * pagadora) — calculado aqui, nunca gravado no legado.
 *
 * Os portoes, nesta ordem (o primeiro que falhar e' o motivo):
 *   1. o contrato tem credor identificado;
 *   2. o credor opera na Atlas — ativo e parceiro (consignataria e' so' credor,
 *      sem acesso: nao ha' quem responda ao pedido);
 *   3. ha' convenio ativo: o do contrato ou, no legado, UM do par.
 *
 * Existe para que a elegibilidade recuse o que o aceite recusaria: antes, o
 * legado aparecia elegivel, pedia senha duas vezes e so' falhava no aceite.
 */
export function convenioDaOrigemRefin(origem, unidadeId, banco, convenios) {
    const nao = (motivo) => ({ convenioId: null, motivo });
    const bancoId = Number(origem.bancoId ?? 0);
    if (!(bancoId > 0))
        return nao("O credor deste contrato ainda não foi identificado.");
    if (!banco || banco.status !== "ativo" || banco.tipo === "consignataria") {
        return nao("O banco deste contrato não opera refinanciamento pela Atlas.");
    }
    const ativo = (c) => c.ativo !== false;
    if (origem.convenioId) {
        const proprio = convenios.find((c) => c.id === origem.convenioId);
        return proprio && ativo(proprio) ? { convenioId: proprio.id, motivo: null } : nao("O convênio deste contrato não está mais ativo.");
    }
    const doPar = convenios.filter((c) => ativo(c) && c.bancoId === bancoId && c.unidadeId === unidadeId);
    if (doPar.length === 0)
        return nao("O banco deste contrato não tem convênio ativo neste órgão.");
    // O indice da 0049 impede dois ativos do par; se acontecer, nao se adivinha.
    if (doPar.length > 1)
        return nao("Há mais de um convênio ativo deste banco neste órgão. Procure a averbadora.");
    return { convenioId: doPar[0].id, motivo: null };
}
/** Soma dos saldos que o contrato novo precisa quitar. */
export function saldoQuitacaoRefin(saldosQuitacao) {
    return round2(saldosQuitacao.reduce((s, v) => s + v, 0));
}
/**
 * O principal cobre a quitação? Independente do troco que o banco informar.
 */
export function cobreQuitacaoRefin(valorFinanciado, saldosQuitacao) {
    const saldo = saldoQuitacaoRefin(saldosQuitacao);
    if (round2(valorFinanciado - saldo) < -0.01) {
        return { saldo, erro: "O valor financiado não cobre o saldo de quitação das origens." };
    }
    return { saldo };
}
/**
 * Troco que o banco digitou (ou o sugerido). A averbadora não calcula Price:
 * só recusa negativo, convênio sem troco e piso do convênio.
 */
export function conferirTrocoRefin(trocoBruto, regras) {
    if (trocoBruto < -0.01)
        return { troco: trocoBruto, erro: "O troco não pode ser negativo." };
    const troco = trocoBruto > 0.01 ? round2(trocoBruto) : 0;
    if (troco > 0 && !regras.permiteTroco)
        return { troco, erro: "O convênio não permite troco no refinanciamento." };
    if (troco > 0 && troco + 0.01 < regras.trocoMinimo) {
        return { troco, erro: `O troco mínimo do convênio é R$ ${regras.trocoMinimo.toFixed(2)}.` };
    }
    return { troco };
}
/**
 * Sugestão de troco: valor financiado − saldo de quitação das origens. O banco
 * confirma ou substitui. Parcela × prazo NÃO serve: soma os juros de todas as
 * parcelas e prometia ao servidor um líquido que o contrato não libera.
 */
export function trocoSugeridoRefin(input) {
    return Math.max(0, round2(input.valorFinanciado - saldoQuitacaoRefin(input.saldosQuitacao)));
}
/**
 * Troco = valor financiado - soma dos saldos de quitacao. Negativo e' recusa:
 * o contrato novo precisa quitar as origens. Quem opera o banco informa a
 * parcela e, se quiser, um troco diferente; esta função cobre o atalho
 * “troco = o que sobra do principal”.
 */
export function calcularTrocoRefin(input) {
    const quitacao = cobreQuitacaoRefin(input.valorFinanciado, input.saldosQuitacao);
    if (quitacao.erro) {
        return { troco: round2(input.valorFinanciado - quitacao.saldo), erro: quitacao.erro };
    }
    return conferirTrocoRefin(round2(input.valorFinanciado - quitacao.saldo), input.regras);
}
/**
 * Quanto de margem NOVA o refinanciamento consome: so' o que a parcela nova
 * excede as parcelas que ela substitui. Ate a aplicacao da ADF as origens
 * continuam descontando e contando na margem — e' por isso que o sucessor so'
 * precisa da diferenca, e nao da parcela inteira.
 */
export function consumoDeMargemDoRefin(input) {
    const origens = input.parcelasOrigem.reduce((s, v) => s + v, 0);
    return Math.max(0, round2(input.parcelaNova - origens));
}
/**
 * O banco respondeu exatamente o que o servidor pediu? Entao o aceite do pedido
 * (dado com senha) vale para a proposta. Sem parcela E prazo pedidos, o
 * servidor nao consentiu com o custo: a resposta vira contraproposta.
 */
export function pedidoIgualProposta(input) {
    if (input.parcelaSolicitada == null || input.prazoSolicitado == null)
        return false;
    if (Math.abs(input.parcelaSolicitada - input.valorParcela) > 0.01)
        return false;
    if (input.valorSolicitado != null && Math.abs(input.valorSolicitado - input.valorFinanciado) > 0.01)
        return false;
    return input.prazoSolicitado === input.prazo;
}
/**
 * Primeira competencia (AAAAMM) do sucessor: a do calendario, empurrada para
 * depois da ultima competencia em que alguma origem ja foi instruida em folha.
 * Sem isso o mesmo mes teria a parcela antiga e a nova — desconto em dobro.
 */
export function primeiraCompetenciaRefin(input) {
    if (!/^\d{6}$/.test(input.padrao))
        return input.padrao;
    const ultimas = input.ultimasDasOrigens.filter((c) => typeof c === "string" && /^\d{6}$/.test(c));
    if (ultimas.length === 0)
        return input.padrao;
    const maior = ultimas.reduce((a, b) => (b > a ? b : a));
    return input.padrao > maior ? input.padrao : somarMes(maior, 1);
}
function somarMes(aaaamm, n) {
    const total = Number(aaaamm.slice(0, 4)) * 12 + (Number(aaaamm.slice(4, 6)) - 1) + n;
    return `${Math.floor(total / 12)}${String((total % 12) + 1).padStart(2, "0")}`;
}
function round2(n) {
    return Math.round((n + Number.EPSILON) * 100) / 100;
}
//# sourceMappingURL=refinanciamento.js.map